import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store';
import useMemoryStore from '@/store/memory';
import { chatsApi } from '@/lib/api/chats';
import { DISCOVERY_FALLBACK } from '@/constants/discoveryFallback';

const EMPTY_HISTORY = Object.freeze({});
const CACHE_TTL = 5 * 60 * 1000;
const MAX_PAGES = 10;
const visits = new Map();
const empty = key => ({ key, items: [], cursor: null, loaded: false, loading: false, error: '', mutedCategories: [], pages: 0, retryAt: 0, fallback: false });
function visitFor(key) {
  let entry = visits.get(key);
  if (!entry || (entry.expires <= Date.now() && !entry.state.loading)) {
    entry = { state: empty(key), history: null, listeners: new Set(), expires: Date.now() + CACHE_TTL };
    if (visits.size >= 8) visits.delete(visits.keys().next().value);
    visits.set(key, entry);
  }
  return entry;
}
function put(entry, state) {
  entry.state = state;
  entry.listeners.forEach(listener => listener());
}
export default function useDiscoveryFeed({ enabled, history = EMPTY_HISTORY }) {
  const userId = useAuthStore(s => s.user?.id);
  const session = useAuthStore(s => s._sessionGeneration);
  const memoryEnabled = useAuthStore(s => s.user?.preferences?.memory?.isEnabled !== false);
  const revision = useMemoryStore(s => s.workStarterPromptsRevision);
  const [refresh, setRefresh] = useState(0);
  const [, render] = useState(0);
  const key = JSON.stringify([userId, session, memoryEnabled, revision, refresh, new Date().toISOString().slice(0, 10)]);
  const current = useRef(null);
  if (current.current?.state.key !== key) current.current = visitFor(key);
  const entry = current.current;
  const historyRef = useRef(history); historyRef.current = history;
  const canLoad = enabled && !!userId;
  const loadMore = useCallback(async () => {
    const state = entry.state;
    if (!canLoad || state.loading || state.retryAt > Date.now() || state.pages >= MAX_PAGES || (state.loaded && !state.cursor && !state.fallback)) return;
    // Freeze history for the whole cursor chain. Visibility/storage rerenders
    // must never invalidate a request or advance pagination on their own.
    if (!entry.history) entry.history = historyRef.current;
    put(entry, { ...state, loading: true, error: '' });
    try {
      let page, restarted = false;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          page = await chatsApi.fetchDiscoveryPage(entry.state.cursor, entry.history);
          if (current.current !== entry) { put(entry, { ...entry.state, loading: false }); return; }
        }
        catch (error) {
          if (current.current !== entry) { put(entry, { ...entry.state, loading: false }); return; }
          if (error.status !== 409 || restarted) throw error;
          restarted = true;
          put(entry, { ...empty(key), loading: true });
          continue;
        }
        if (!page.pending) break;
        if (attempt < 3) await new Promise(resolve => setTimeout(resolve, Math.min(10000, Math.max(2000, page.retryAfterMs || 2000))));
        if (current.current !== entry) { put(entry, { ...entry.state, loading: false }); return; }
      }
      if (!page || page.pending) throw new Error('Suggestions are still refreshing. Please try again shortly.');
      const previous = entry.state.fallback ? [] : entry.state.items;
      const known = new Set(previous.map(item => item.id));
      const incoming = (page.items || []).filter(item => !known.has(item.id) && known.add(item.id));
      const pages = entry.state.pages + 1;
      put(entry, { ...entry.state, items: [...previous, ...incoming], pages,
        cursor: pages < MAX_PAGES ? page.nextCursor || null : null, fallback: false, retryAt: 0,
        mutedCategories: page.mutedCategories || [], loaded: true, loading: false });
    } catch (error) {
      const seconds = Number(error.data?.retryAfterSeconds);
      const retryAt = Date.now() + (error.status === 429 ? (Number.isFinite(seconds) && seconds > 0 ? seconds : 60) * 1000 : 5000);
      const noIdeas = !entry.state.items.length;
      put(entry, { ...entry.state, loading: false, retryAt,
        ...(noIdeas ? { items: DISCOVERY_FALLBACK, fallback: true } : {}),
        error: error.status === 429 ? 'Fresh suggestions will be available shortly.' : error.message || 'Could not refresh ideas. Please try again.' });
    }
  }, [canLoad, entry, key]);
  useEffect(() => {
    const listener = () => render(value => value + 1);
    entry.listeners.add(listener);
    if (canLoad && !entry.state.loaded && !entry.state.error) loadMore();
    return () => entry.listeners.delete(listener);
  }, [canLoad, entry, loadMore]);
  useEffect(() => {
    if (!entry.state.retryAt || entry.state.retryAt <= Date.now()) return undefined;
    const timer = setTimeout(() => render(value => value + 1), Math.min(2147483647, entry.state.retryAt - Date.now() + 1));
    return () => clearTimeout(timer);
  }, [entry, entry.state.retryAt]);
  const dismiss = useCallback(async id => {
    try {
      await chatsApi.updateDiscoveryPreferences({ action: 'dismiss', id });
      put(entry, { ...entry.state, items: entry.state.items.filter(item => item.id !== id) });
    } catch { put(entry, { ...entry.state, error: 'Could not dismiss this idea. Please try again.' }); }
  }, [entry]);
  const mute = useCallback(async mutedCategories => {
    try {
      await chatsApi.updateDiscoveryPreferences({ action: 'categories', mutedCategories });
      visits.delete(key);
      if (current.current === entry) setRefresh(n => n + 1);
    } catch { put(entry, { ...entry.state, error: 'Could not save your feed preferences.' }); }
  }, [entry, key]);
  return { ...entry.state, retryBlocked: entry.state.retryAt > Date.now(), loadMore, dismiss, mute };
}
